import { useState, FC, ReactNode } from 'react';
import { NavLink as RouterLink } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Link, Collapse, ListItem, Box } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

interface NavItemProps {
  children?: ReactNode;
  className?: string;
  depth: number;
  href?: string;
  icon?: any;
  info?: any;
  open?: boolean;
  title: string;
  onClick?: () => void;
}

const NavItem: FC<NavItemProps> = ({
  children,
  className,
  depth,
  href,
  icon: Icon,
  info: Info,
  open: openProp,
  title,
  onClick,
  ...rest
}) => {
  const [open, setOpen] = useState<boolean>(openProp);

  const baseSx = {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    px: 1.5,
    py: '9px',
    borderRadius: '8px',
    transition: 'background-color 0.15s ease, color 0.15s ease',
  };

  const hoverSx = {
    '&:hover': {
      backgroundColor: 'rgba(0,0,0,0.04)',
      color: 'text.primary',
    },
  };

  const itemContent = (
    <>
      {Icon && (
        <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, width: 18, height: 18 }}>
          <Icon size={18} />
        </Box>
      )}
      <Box
        component="span"
        className="nav-title"
        sx={{ flex: 1, fontSize: '0.875rem', fontWeight: 500, letterSpacing: '-0.01em' }}
      >
        {title}
      </Box>
      {Info && <Info />}
    </>
  );

  if (children) {
    return (
      <ListItem
        disableGutters
        className={className}
        {...rest}
        sx={{ display: 'block', p: 0, mb: '2px' }}
      >
        <Box
          onClick={() => setOpen((prev) => !prev)}
          sx={{ ...baseSx, color: 'text.secondary', cursor: 'pointer', ...hoverSx }}
        >
          {itemContent}
          {open ? (
            <ExpandLessIcon sx={{ fontSize: 16, flexShrink: 0 }} />
          ) : (
            <ExpandMoreIcon sx={{ fontSize: 16, flexShrink: 0 }} />
          )}
        </Box>
        <Collapse in={open}>{children}</Collapse>
      </ListItem>
    );
  }

  return (
    <ListItem
      disableGutters
      className={className}
      {...rest}
      sx={{ display: 'flex', p: 0, mb: '2px' }}
    >
      {onClick ? (
        <Box
          onClick={onClick}
          sx={{ ...baseSx, color: 'text.secondary', cursor: 'pointer', ...hoverSx }}
        >
          {itemContent}
        </Box>
      ) : (
        <Link
          component={RouterLink}
          to={href ?? '#'}
          underline="none"
          activeClassName="active"
          sx={{
            ...baseSx,
            color: 'text.secondary',
            ...hoverSx,
            '&.active': {
              backgroundColor: 'secondary.main',
              color: 'white',
              '& .nav-title': { fontWeight: 600 },
              '& .MuiChip-colorSecondary': {
                backgroundColor: 'rgba(255,255,255,0.2)',
                '& .MuiChip-label': { color: 'white' },
              },
            },
          }}
        >
          {itemContent}
        </Link>
      )}
    </ListItem>
  );
};

NavItem.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  depth: PropTypes.number.isRequired,
  href: PropTypes.string,
  icon: PropTypes.elementType,
  info: PropTypes.elementType,
  open: PropTypes.bool,
  title: PropTypes.string.isRequired,
};

NavItem.defaultProps = {
  open: true,
};

export default NavItem;
